import { useState } from 'react';
import { QUEST_TASK_TITLE_MAX, questTaskTitleError } from '@aegislab/shared';
import { Modal } from '../../atoms/Modal';
import { ModalFooter } from '../../molecules/ModalFooter';
import * as s from './CheckOutModal.css';

export interface CheckOutModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * 오늘 실적(차액)과 (계획과 다르면) 사유를 확정. 같으면 사유는 null.
   * 세 번째 인자는 사람이 누적 완료 건수 칸에 적은 원값 — 있으면 호출부가 그것만 서버에 보내고
   * 서버가 차액을 다시 확정한다(클라 계산은 미리보기). 오늘 칸으로 적었으면 생략된다.
   * 네 번째 인자는 결과 한 문장으로 고친 제목 — 바꿨을 때만 온다.
   */
  onConfirm: (
    actualValue: number,
    varianceReason: string | null,
    cumulativeValue?: number,
    title?: string
  ) => void;
  /**
   * 체크아웃할 태스크 제목. 주면 "결과 한 문장" 칸에 담겨 고칠 수 있다(결정 2026-10-01) —
   * 착수 때는 결과를 모르니 끝내는 이 자리에서 결과로 고쳐 쓴다.
   */
  taskTitle?: string;
  /**
   * 체크인 시 입력한 계획 수치. `null` = 계획 없이 체크인한 정량 태스크 —
   * 0 으로 접으면 실제값이 무엇이든 variance 가 되어 없는 계획에 대한 사유를 강제한다.
   */
  plannedValue: number | null;
  /**
   * 같은 체크포인트에 지금까지 적립된 본인 값(게이지 contributions). 있으면 두 칸 연동 —
   * "오늘 한 건수" ⇄ "누적 완료 건수" — 로 바뀐다. null/undefined = 게이지 없음(정성·미로딩) → 한 칸.
   */
  priorValue?: number | null;
  /** 체크포인트 셀 단위(명·건). 표시 전용. */
  unit?: string | null;
  loading?: boolean;
}

/**
 * 정량 태스크 체크아웃(투스텝) 모달.
 * 실제 작업량은 빈 입력에서 시작한다 — 계획값 prefill 시 무편집 확정으로
 * variance가 0으로 도배되어 계획 정확도 신호가 형해화된다(R3). 계획은 상단 표시.
 * 실제 ≠ 계획이면 사유 textarea가 나타나고 필수가 된다(빈 사유 시 primary 비활성).
 *
 * 두 칸 연동(제보 #115): 시트를 드래그해서 세면 누적만 나와 사람들이 그 값을 그날 실적 칸에 그대로
 * 적었고, 시스템은 매일 더했다. 지금까지 적립된 값을 보여 주고 아는 쪽(오늘/누적)을 적게 한다.
 * 한 칸만 적으면 다른 칸이 계산돼 뜨고, 두 칸을 다 손으로 적으면 `적립 + 오늘 = 누적`을 검산한다.
 * 강제 이중 입력이 아닌 이유: 강제하면 한 칸을 다른 칸에서 만들어 넣어 검산이 항등식이 된다
 * (계획값을 강제했을 때 8건 전부 value == actualValue 복사였던 실측).
 */
export function CheckOutModal({
  open,
  onClose,
  onConfirm,
  taskTitle,
  plannedValue,
  priorValue,
  unit,
  loading = false,
}: CheckOutModalProps) {
  const [actual, setActual] = useState('');
  const [cumulative, setCumulative] = useState('');
  const [reason, setReason] = useState('');
  const [titleDraft, setTitleDraft] = useState(taskTitle ?? '');

  // 열릴 때마다 빈 입력으로 초기화 (태스크가 바뀌어도 정합).
  const [previousInput, setPreviousInput] = useState({ open, plannedValue, priorValue });
  if (
    previousInput.open !== open ||
    previousInput.plannedValue !== plannedValue ||
    previousInput.priorValue !== priorValue
  ) {
    setPreviousInput({ open, plannedValue, priorValue });
    if (open) {
      setActual('');
      setCumulative('');
      setReason('');
      setTitleDraft(taskTitle ?? '');
    }
  }

  // 계획은 값이 아니라 흐린 안내로만 — 채우면 R3(무편집 확정) 이 되살아난다.
  const planHint = plannedValue != null ? `계획 ${plannedValue}` : undefined;
  const linked = priorValue != null;
  const prior = priorValue ?? 0;
  const unitText = unit ?? '';

  const parseNonNegative = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const todayTyped = parseNonNegative(actual);
  const cumulativeTyped = parseNonNegative(cumulative);
  // 빈 칸 = 손대지 않음. 지우면 다시 연동 대상으로 돌아온다.
  const todayTouched = actual.trim() !== '';
  const cumulativeTouched = cumulative.trim() !== '';

  // 오늘 실적(차액)의 단일 기준값 — variance·확정 모두 이걸 본다.
  let effectiveToday: number | null;
  let cumulativeShown = cumulative;
  let todayShown = actual;
  let belowPrior = false;
  let mismatch = false;
  if (!linked) {
    effectiveToday = todayTyped;
  } else if (cumulativeTouched && !todayTouched) {
    belowPrior = cumulativeTyped != null && cumulativeTyped < prior;
    effectiveToday = cumulativeTyped != null && !belowPrior ? cumulativeTyped - prior : null;
    todayShown = effectiveToday != null ? String(effectiveToday) : '';
  } else if (todayTouched && !cumulativeTouched) {
    effectiveToday = todayTyped;
    cumulativeShown = todayTyped != null ? String(prior + todayTyped) : '';
  } else if (todayTouched && cumulativeTouched) {
    belowPrior = cumulativeTyped != null && cumulativeTyped < prior;
    mismatch =
      !belowPrior &&
      todayTyped != null &&
      cumulativeTyped != null &&
      prior + todayTyped !== cumulativeTyped;
    effectiveToday = !belowPrior && !mismatch ? todayTyped : null;
  } else {
    effectiveToday = null;
  }

  // const 로 받아야 아래 클로저(handleConfirm)에서도 null 배제가 유지된다.
  const today = effectiveToday;
  const valid = today != null;
  // 계획이 없으면 비교 대상이 없다 — variance 자체가 성립하지 않으므로 사유도 묻지 않는다.
  // 연동 모드에서도 비교는 누적이 아니라 차액(오늘 한 건수)과 한다 — 누적과 비교하면 거짓 힌트.
  const differs = today != null && plannedValue != null && today !== plannedValue;
  const reasonMissing = differs && !reason.trim();
  const diff = today != null && plannedValue != null ? today - plannedValue : 0;

  // 결과 제목 — 서버와 같은 제목 계약으로 검사하고, 바꿨을 때만 넘긴다.
  const trimmedTitle = titleDraft.trim();
  const titleError = questTaskTitleError(trimmedTitle);
  const titleBlocked = taskTitle != null && (!trimmedTitle || titleError != null);
  const changedTitle = taskTitle != null && trimmedTitle !== taskTitle ? trimmedTitle : undefined;

  const handleConfirm = () => {
    if (today == null || reasonMissing || titleBlocked) return;
    const trimmedReason = differs ? reason.trim() : null;
    const cumulativeArg =
      linked && cumulativeTouched && cumulativeTyped != null ? cumulativeTyped : undefined;
    if (changedTitle !== undefined) {
      onConfirm(today, trimmedReason, cumulativeArg, changedTitle);
      return;
    }
    if (cumulativeArg !== undefined) {
      onConfirm(today, trimmedReason, cumulativeArg);
      return;
    }
    onConfirm(today, trimmedReason);
  };

  const varianceBlock = (
    <div className={s.varianceFields} aria-live="polite">
      {differs && (
        <>
          <p className={diff > 0 ? s.diffHint.over : s.diffHint.under}>
            계획보다 {diff > 0 ? `${diff} 많음` : `${Math.abs(diff)} 적음`}
          </p>
          <label className={s.label} htmlFor="checkout-reason">
            계획과 다른 이유 <span className={s.required}>*</span>
          </label>
          <textarea
            id="checkout-reason"
            className={s.textarea}
            placeholder="예상보다 높거나 낮았던 이유를 적어주세요."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={1000}
            aria-invalid={reasonMissing}
          />
          <div className={s.charCount}>{reason.length}/1000</div>
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={linked ? '체크아웃 — 오늘 실적' : '체크아웃 — 실제 작업량'}
      footer={
        <ModalFooter
          primaryLabel="체크아웃"
          onPrimaryClick={handleConfirm}
          onSecondaryClick={onClose}
          loading={loading}
          primaryDisabled={!valid || reasonMissing || titleBlocked}
        />
      }
    >
      <div className={s.content}>
        {taskTitle != null && (
          <div className={s.titleField}>
            <label className={s.label} htmlFor="checkout-title">
              결과 한 문장
            </label>
            <input
              id="checkout-title"
              className={s.input}
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              maxLength={QUEST_TASK_TITLE_MAX}
              aria-invalid={titleBlocked}
              aria-describedby="checkout-title-hint"
            />
            <p
              id="checkout-title-hint"
              className={titleError ? s.checkHint : s.titleHint}
              aria-live="polite"
            >
              {titleError ?? '착수할 때 쓴 제목이 들어 있어요. 결과가 달라졌으면 고쳐 주세요.'}
            </p>
          </div>
        )}

        {linked ? (
          <>
            <p className={s.helpText}>
              오늘 한 건수와 누적 완료 건수 중 아는 쪽을 적어 주세요. 다른 쪽은 계산돼요.
            </p>

            {plannedValue != null && (
              <dl className={s.planRow}>
                <dt className={s.planLabel}>계획</dt>
                <dd className={s.planValue}>{plannedValue}</dd>
              </dl>
            )}

            <dl className={s.planRow}>
              <dt className={s.planLabel}>지금까지 적립</dt>
              <dd className={s.planValue}>
                {prior}
                {unitText && <span className={s.unitSuffix}>{unitText}</span>}
              </dd>
            </dl>

            <div className={s.twoFields}>
              <div className={s.field}>
                <label className={s.label} htmlFor="checkout-today">
                  오늘 한 건수
                  {!todayTouched && cumulativeTouched && <span className={s.calcTag}>계산됨</span>}
                </label>
                <input
                  id="checkout-today"
                  className={`${s.input} ${!todayTouched && cumulativeTouched ? s.calcInput : ''}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  placeholder={planHint}
                  value={todayShown}
                  onChange={(e) => setActual(e.target.value)}
                />
              </div>
              <span className={s.arrow} aria-hidden>
                ⇄
              </span>
              <div className={s.field}>
                <label className={s.label} htmlFor="checkout-cumulative">
                  누적 완료 건수
                  {!cumulativeTouched && todayTouched && <span className={s.calcTag}>계산됨</span>}
                </label>
                <input
                  id="checkout-cumulative"
                  className={`${s.input} ${!cumulativeTouched && todayTouched ? s.calcInput : ''}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={cumulativeShown}
                  onChange={(e) => setCumulative(e.target.value)}
                />
              </div>
            </div>

            <div aria-live="polite">
              {belowPrior && (
                <p className={s.checkHint}>
                  누적 완료 건수가 지금까지 적립된 {prior}
                  {unitText}보다 작아요. 정정이 필요하면 팀장에게 요청해 주세요.
                </p>
              )}
              {mismatch && todayTyped != null && (
                <p className={s.checkHint}>
                  지금까지 {prior}
                  {unitText}에 오늘 {todayTyped}
                  {unitText}이면 누적은 {prior + todayTyped}
                  {unitText}이어야 해요. 어느 쪽이 맞나요?
                </p>
              )}
            </div>

            {varianceBlock}
          </>
        ) : (
          <>
            <p className={s.helpText}>
              체크인 때 계획한 수치를 실제 작업량으로 확정하는 단계예요. 값이 다르면 사유를 남겨요.
            </p>

            <dl className={s.planRow}>
              <dt className={s.planLabel}>계획</dt>
              <dd className={s.planValue}>{plannedValue ?? '없음'}</dd>
            </dl>

            <label className={s.label} htmlFor="checkout-actual">
              실제 작업량 <span className={s.required}>*</span>
            </label>
            <input
              id="checkout-actual"
              className={s.input}
              type="number"
              inputMode="decimal"
              min={0}
              placeholder={planHint}
              value={actual}
              onChange={(e) => setActual(e.target.value)}
            />

            {varianceBlock}
          </>
        )}
      </div>
    </Modal>
  );
}

export default CheckOutModal;
